"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Input,
  HStack,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";

interface BoostedKeywordsProps {
  value: string[] | null | undefined;
  onChange: (value: string[] | null) => Promise<boolean> | void;
}

export const BoostedKeywords: React.FC<BoostedKeywordsProps> = ({
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Convert array to comma-separated string for the input
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setInputValue(value.join(", "));
    } else {
      setInputValue("");
    }
  }, [value]);

  const handleBlur = async () => {
    const keywords = inputValue
      .split(",")
      .map((keyword) => keyword.trim())
      .filter((keyword) => keyword.length > 0);

    const newValue = keywords.length > 0 ? keywords : null;

    if (JSON.stringify(newValue) !== JSON.stringify(value)) {
      setIsUpdating(true);
      try {
        await onChange(newValue);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <FormControl>
      <HStack align="center" mb={1} spacing={2}>
        <FormLabel
          fontSize="sm"
          fontWeight="medium"
          color="secondary.700"
          mb={0}
        >
          Boosted Keywords
        </FormLabel>
        <Tooltip
          label="Keywords that should be recognized more accurately during transcription"
          placement="top"
        >
          <IconButton
            aria-label="Info"
            icon={<InfoIcon />}
            size="xs"
            variant="ghost"
            color="gray.600"
            ml={-2}
          />
        </Tooltip>
      </HStack>

      <Text fontSize="xs" color="gray.600" mb={2}>
        Provide a customized list of keywords to expand our models&apos;
        vocabulary.
      </Text>

      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        placeholder="Split by comma. Example: Walmart, Amazon, Google"
        size="sm"
        bg="white"
        borderColor="gray.300"
        _hover={{ borderColor: "gray.400" }}
        _focus={{ borderColor: "gray.400", boxShadow: "none" }}
        isDisabled={isUpdating}
      />
    </FormControl>
  );
};
